import { PlatformIdentityService } from '../identity/platform-identity.service';
import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, catchError, EMPTY, filter, firstValueFrom, map, Observable, shareReplay, throwError, timeout, timer} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {AccountInfo} from '@azure/msal-browser';

export function getMsalAccountFingerprint(account: AccountInfo | null): string | null {
    const homeAccountId = account?.homeAccountId?.trim();
    const tenantId = account?.tenantId?.trim();
    return homeAccountId && tenantId ? `${tenantId}:${homeAccountId}` : null;
}

/**
 * Client information in the user rights response
 */
export interface ClientRightsInfo {
    Name: string;
    Logo: string;
    Description: string;
    Domain: string;
    id: string;
}

/**
 * Enabled product information in the user rights response
 */
export interface EnabledProductInfo {
    name: string;
    access: string;
}

/**
 * Module access information in the user rights response
 */
export interface ModuleAccessInfo {
    name: string;
    access: string;
}

/**
 * Client with enabled products in the user rights response
 */
export interface ClientWithProducts {
    client: ClientRightsInfo;
    enabledProducts: EnabledProductInfo[];
    moduleAccess?: ModuleAccessInfo[];
}

/**
 * User rights response from the Shine Auth service
 */
export interface UserRightsResponse {
    status: string;
    data: {
        rightsVersion?: number;
        clients: ClientWithProducts[];
    };
}

/**
 * Read-only user-management row for a selected client.
 */
export interface UserManagementUserRow {
    id: string;
    email?: string;
    access: string;
    enabledProducts: EnabledProductInfo[];
    moduleAccess: ModuleAccessInfo[];
}

/**
 * Read-only selected-client user-management response from Shine Auth.
 */
export interface UserManagementUsersResponse {
    status: string;
    data: {
        rightsVersion: number;
        client: {
            Name: string;
            Domain: string;
            id: string;
        };
        users: UserManagementUserRow[];
    };
}

/**
 * Service for retrieving user and client information from the Shine Auth service.
 * This service communicates with the Shine Auth service, which retrieves data from HubSpot.
 */
@Injectable({
    providedIn: 'root'
})
export class ShineUserInfoService {
    private http = inject(HttpClient);
    private identity = inject(PlatformIdentityService);
    private readonly USER_RIGHTS_CACHE_VERSION = 'v2';

    private readonly baseUrl: string;

    // Cache for user rights data
    private userRightsCache$: Observable<UserRightsResponse> | null = null;
    private cacheTimestamp: number = 0;
    private cachedRightsAccountFingerprint: string | null = null;
    private readonly CACHE_DURATION_MS: number = 30_000; // visible-session polling interval
    private readonly LOCAL_STORAGE_KEY: string = `shine_user_rights_cache_${this.USER_RIGHTS_CACHE_VERSION}`;
    private readonly LOCAL_STORAGE_TIMESTAMP_KEY: string =
        `shine_user_rights_cache_timestamp_${this.USER_RIGHTS_CACHE_VERSION}`;
    private inFlightRequest$: Observable<UserRightsResponse> | null = null;
    private inFlightAccountFingerprint: string | null = null;
    private cacheGeneration = 0;
    private refreshScheduled = false;

    // Subject to trigger cache refresh
    private refreshCache$ = new BehaviorSubject<boolean>(false);

    /**
     * Initializes a new instance of the ShineUserInfoService class.
     * @param http The HTTP client.
     */
    constructor() {
        this.baseUrl = environment.authBaseServerUrl || 'https://auth.shine.intuvio.com';
    }

    /**
     * Clears the user rights cache, forcing a new API call on the next request.
     */
    public clearCache(): void {
        console.log('Clearing user rights cache');
        this.cacheGeneration++;
        this.userRightsCache$ = null;
        this.cacheTimestamp = 0;
        this.cachedRightsAccountFingerprint = null;
        this.inFlightRequest$ = null;
        this.inFlightAccountFingerprint = null;
        this.refreshScheduled = false;
        localStorage.removeItem(this.LOCAL_STORAGE_KEY);
        localStorage.removeItem(this.LOCAL_STORAGE_TIMESTAMP_KEY);
        this.refreshCache$.next(true);
    }

    /**
     * Checks if the cache is still valid based on the cache duration.
     * @returns True if the cache is valid, false otherwise.
     */
    private isCacheValid(): boolean {
        const now = Date.now();
        return this.userRightsCache$ !== null && (now - this.cacheTimestamp) < this.CACHE_DURATION_MS;
    }

    /**
     * Gets user rights information from the Shine Auth service.
     * This includes a list of clients the user can access and their enabled products with access levels.
     * Uses an account-bound in-memory cache to reduce API calls.
     * @param forceRefresh If true, forces a refresh of the cache.
     * @returns An observable that resolves to the user rights information.
     */
    getUserRights(forceRefresh: boolean = false): Observable<UserRightsResponse> {
        // Persisted rights are no longer authoritative. Remove any payload left
        // by earlier versions before resolving rights for the current account.
        localStorage.removeItem(this.LOCAL_STORAGE_KEY);
        localStorage.removeItem(this.LOCAL_STORAGE_TIMESTAMP_KEY);

        const accountFingerprint = this.identity.fingerprint;
        if (!accountFingerprint) {
            this.clearCache();
            return throwError(() => new Error('No active MSAL account available for user rights'));
        }

        if (
            (this.cachedRightsAccountFingerprint && this.cachedRightsAccountFingerprint !== accountFingerprint)
            || (this.inFlightAccountFingerprint && this.inFlightAccountFingerprint !== accountFingerprint)
        ) {
            this.clearCache();
        }

        // A forced caller joins a currently authoritative request. The caller that
        // created it has already cleared identity-derived cache state; replacing it
        // would turn redirect-time guards into duplicate rights requests.
        if (forceRefresh && this.inFlightRequest$ && this.inFlightAccountFingerprint === accountFingerprint) {
            return this.inFlightRequest$;
        }

        // If force refresh is requested, invalidate any cached or older-identity
        // response before creating the next authoritative request.
        if (forceRefresh) {
            console.debug('Force refresh requested, clearing user rights cache');
            this.clearCache();
        }

        // Reuse the current in-flight request to avoid duplicate HTTP calls
        if (!forceRefresh && this.inFlightRequest$) {
            return this.inFlightRequest$;
        }

        // If we have a valid in-memory cache, use it
        if (this.isCacheValid() && this.cachedRightsAccountFingerprint === accountFingerprint) {
            console.debug('Using in-memory cached user rights data');

            // Trigger an async refresh in the background after a delay
            this.triggerAsyncRefresh();

            return this.userRightsCache$!;
        }

        // If a request is already running (e.g., after force refresh), reuse it
        if (this.inFlightRequest$ && this.inFlightAccountFingerprint === accountFingerprint) {
            return this.inFlightRequest$;
        }

        console.debug('Getting user rights from Shine Auth (cache invalid or not set)');

        // Create a new cache observable
        const requestGeneration = this.cacheGeneration;
        const requestAccountFingerprint = accountFingerprint;
        const request$ = this.http.get<UserRightsResponse>(`${this.baseUrl}api/user/rights`)
            .pipe(
                timeout(45_000),
                // An older identity's request can still complete after clearCache().
                // Do not expose or cache that result after the generation changes.
                filter(() => requestGeneration === this.cacheGeneration
                    && requestAccountFingerprint === this.identity.fingerprint),
                map(response => {
                    if (response?.status !== 'success') {
                        console.warn(`Failed to get user rights: ${response?.status}`);
                        throw new Error('Failed to get user rights');
                    }

                    // Update cache timestamp
                    this.cacheTimestamp = Date.now();
                    this.cachedRightsAccountFingerprint = requestAccountFingerprint;
                    console.debug('User rights cache updated at', new Date(this.cacheTimestamp).toLocaleTimeString());

                    return response;
                }),
                catchError(error => {
                    if (
                        requestGeneration !== this.cacheGeneration
                        || requestAccountFingerprint !== this.identity.fingerprint
                    ) {
                        return EMPTY;
                    }

                    console.error('Error getting user rights:', error);
                    this.userRightsCache$ = null;
                    this.cachedRightsAccountFingerprint = null;
                    return throwError(() => error);
                }),
                finalize(() => {
                    if (this.inFlightRequest$ === request$) {
                        this.inFlightRequest$ = null;
                        this.inFlightAccountFingerprint = null;
                    }
                }),
                // Share the same observable for all subscribers
                shareReplay(1)
            );

        this.userRightsCache$ = request$;
        this.inFlightRequest$ = request$;
        this.inFlightAccountFingerprint = requestAccountFingerprint;

        return request$;
    }

    /**
     * Gets read-only user-management rows for the selected client.
     * @param clientId Selected client domain or HubSpot/company identifier.
     */
    getClientUsers(clientId: string): Observable<UserManagementUsersResponse> {
        const normalizedClientId = clientId.trim();
        return this.http.get<UserManagementUsersResponse>(
            `${this.baseUrl}api/user/client-users?clientId=${encodeURIComponent(normalizedClientId)}`
        ).pipe(
            map(response => {
                if (response?.status !== 'success') {
                    throw new Error('Failed to get client users');
                }
                return response;
            }),
            catchError(error => {
                console.error('Error getting client users:', error);
                throw error;
            })
        );
    }

    /**
     * Triggers an asynchronous refresh of the user rights data after a short delay.
     * This allows the UI to load quickly with cached data while fresh data is fetched in the background.
     */
    private triggerAsyncRefresh(): void {
        if (this.refreshScheduled || this.inFlightRequest$) {
            return;
        }

        this.refreshScheduled = true;
        const scheduledGeneration = this.cacheGeneration;

        // Wait a short delay before refreshing to allow the UI to load first
        timer(2000).subscribe(() => {
            this.refreshScheduled = false;

            if (scheduledGeneration !== this.cacheGeneration || this.inFlightRequest$) {
                return;
            }

            console.debug('Triggering async refresh of user rights data');

            // Make a fresh API call
            firstValueFrom(this.getUserRights(true))
                .then(() => {
                    console.debug('Async refresh completed, updating cache');
                })
                .catch(error => {
                    console.error('Error in async refresh of user rights:', error);
                });
        });
    }
}
